import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { TerminalIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cliCallback } from '../../../shared/cli-callback'

export const Route = createFileRoute('/_authed/cli/authorize')({
	validateSearch: (search: Record<string, unknown>) => ({
		callback: typeof search.callback === 'string' ? search.callback : undefined,
		state: typeof search.state === 'string' ? search.state : undefined
	}),
	component: CliAuthorize
})

function CliAuthorize() {
	const { callback, state } = Route.useSearch()
	const { user } = Route.useRouteContext()
	const target = cliCallback(callback, state)
	const [phase, setPhase] = useState<'ready' | 'sending' | 'done' | 'cancelled' | 'failed'>('ready')
	const [error, setError] = useState('')

	async function approve() {
		if (!target || phase !== 'ready') return
		setPhase('sending')
		try {
			const grant = await fetch('/api/cli/token', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ grant_type: 'browser_session', callback, state }),
				credentials: 'same-origin', redirect: 'error', cache: 'no-store',
				signal: AbortSignal.timeout(10000)
			})
			if (!grant.ok) throw new Error(((await grant.json().catch(() => null)) as { error?: string } | null)?.error ?? 'Could not connect the CLI. Run edclub auth login again.')
			// The token response as is: { access_token, expires_in, refresh_token, refresh_expires_in }.
			const delivered = await fetch(target, {
				method: 'POST', headers: { 'Content-Type': 'application/json' }, body: await grant.text(),
				credentials: 'omit', redirect: 'error', cache: 'no-store', referrerPolicy: 'no-referrer',
				signal: AbortSignal.timeout(10000)
			})
			if (!delivered.ok) throw new Error('The CLI did not accept the session. Run edclub auth login again.')
			setPhase('done')
		} catch (cause) {
			setError(cause instanceof TypeError || (cause instanceof DOMException)
				? 'Could not reach the CLI. Check your terminal and run edclub auth login again. Allow local network access if your browser asks.'
				: cause instanceof Error ? cause.message : 'Could not connect the CLI. Run edclub auth login again.')
			setPhase('failed')
		}
	}

	return (
		<Card className="mx-auto mt-8 max-w-lg gap-6 p-4 shadow-sm">
			<CardHeader className="gap-3">
				<TerminalIcon className="size-8 text-primary" aria-hidden />
				<CardTitle><h1 className="text-2xl font-bold">Connect EdClub CLI</h1></CardTitle>
				<CardDescription>Let the CLI on this computer act as you.</CardDescription>
			</CardHeader>
			<CardContent className="grid gap-5">
				{!target ? <p role="alert">Invalid login link. Run <code>edclub auth login</code> in your terminal to get a new link.</p> : <>
					{phase === 'done' ? <p role="status">CLI connected. Return to your terminal. You can close this tab.</p>
						: phase === 'cancelled' ? <p role="status">Cancelled. No token was sent to the CLI.</p>
						: <>
							<p>Connect as <strong>{user.email}</strong>. Internal tools will accept the CLI as you, with your current permissions.</p>
							<p className="rounded-md bg-muted p-3 text-sm">Only approve if you just ran <code>edclub auth login</code> on this computer. The CLI's credentials will be sent to <span className="break-all font-mono">{callback}</span>.</p>
							{phase === 'failed' ? <p role="alert" className="text-sm text-destructive">{error}</p> : <div className="flex flex-wrap gap-3">
								<Button onClick={approve} disabled={phase === 'sending'}>{phase === 'sending' ? 'Connecting…' : 'Connect CLI'}</Button>
								<Button variant="outline" onClick={() => setPhase('cancelled')} disabled={phase === 'sending'}>Cancel</Button>
							</div>}
						</>}
					<p className="text-sm text-muted-foreground">The CLI stays signed in for 30 days after it was last used, even after you sign out here, and picks up role changes and bans within 15 minutes. <code>edclub auth logout</code> revokes it; it does not sign you out of the portal.</p>
				</>}
			</CardContent>
		</Card>
	)
}
